import React, { useEffect, useRef, useState } from 'react';
import {
  Activity,
  Pill,
  Shield,
  Syringe,
  ClipboardList,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import PatientProblemListTab from './PatientProblemListTab';
import PatientAllergyListTab from './PatientAllergyListTab';
import PatientMedicationListTab from './PatientMedicationListTab';
import PatientImmunizationsTab from './PatientImmunizationsTab';
import PatientVitalsTab from './PatientVitalsTab';

interface PatientChartTabProps {
  patientId: string;
  allergiesNeedReconciliation?: boolean;
  /** Optional anchor to scroll to on mount (e.g., from a query-param redirect). */
  initialAnchor?: ChartAnchor;
}

type ChartAnchor =
  | 'problems'
  | 'allergies'
  | 'medications'
  | 'immunizations'
  | 'vitals';

const SECTIONS: Array<{
  id: ChartAnchor;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'problems', label: 'Problems', icon: ClipboardList },
  { id: 'allergies', label: 'Allergies', icon: Shield },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'immunizations', label: 'Immunizations', icon: Syringe },
  { id: 'vitals', label: 'Vitals', icon: Activity },
];

/**
 * Long-scroll chart with sticky section anchors. Replaces the 5 separate
 * "Clinical" sidebar tabs (Problem List, Allergies, Medication List,
 * Immunizations, Vitals) with one navigable view. The clinician sees
 * everything together — matches Elation-style longitudinal chart.
 */
export default function PatientChartTab({
  patientId,
  allergiesNeedReconciliation,
  initialAnchor,
}: PatientChartTabProps) {
  const [active, setActive] = useState<ChartAnchor>(
    initialAnchor || 'problems',
  );
  const sectionRefs = useRef<Record<ChartAnchor, HTMLElement | null>>({
    problems: null,
    allergies: null,
    medications: null,
    immunizations: null,
    vitals: null,
  });

  useEffect(() => {
    if (initialAnchor) {
      const el = sectionRefs.current[initialAnchor];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [initialAnchor]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActive(visible[0].target.id as ChartAnchor);
        }
      },
      { rootMargin: '-30% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    for (const id of Object.keys(sectionRefs.current) as ChartAnchor[]) {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: ChartAnchor) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className='flex gap-6'>
      <nav className='hidden w-44 flex-shrink-0 lg:block'>
        <ul className='sticky top-24 space-y-0.5'>
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <li key={s.id}>
                <button
                  type='button'
                  onClick={() => scrollTo(s.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                  )}
                >
                  <Icon className='h-4 w-4' />
                  {s.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className='min-w-0 flex-1 space-y-8'>
        <ChartSection
          id='problems'
          ref={(el) => (sectionRefs.current.problems = el)}
        >
          <PatientProblemListTab patientId={patientId} />
        </ChartSection>

        <ChartSection
          id='allergies'
          ref={(el) => (sectionRefs.current.allergies = el)}
        >
          <PatientAllergyListTab
            patientId={patientId}
            needsReconciliation={allergiesNeedReconciliation}
          />
        </ChartSection>

        <ChartSection
          id='medications'
          ref={(el) => (sectionRefs.current.medications = el)}
        >
          <PatientMedicationListTab patientId={patientId} />
        </ChartSection>

        <ChartSection
          id='immunizations'
          ref={(el) => (sectionRefs.current.immunizations = el)}
        >
          <PatientImmunizationsTab patientId={patientId} />
        </ChartSection>

        <ChartSection
          id='vitals'
          ref={(el) => (sectionRefs.current.vitals = el)}
        >
          <PatientVitalsTab patientId={patientId} />
        </ChartSection>
      </div>
    </div>
  );
}

interface ChartSectionProps {
  id: ChartAnchor;
  children: React.ReactNode;
}

const ChartSection = React.forwardRef<HTMLElement, ChartSectionProps>(
  function ChartSection({ id, children }, ref) {
    return (
      <section
        id={id}
        ref={ref}
        className='scroll-mt-20 rounded-lg border border-slate-200 bg-white p-1'
      >
        {children}
      </section>
    );
  },
);

export type { ChartAnchor };
